<template>
  <div class="gift-card-preview-container">
    <div class="gift-card__head">
      <PageHeadlineWithFourSteps
        title="プレビュー"
        :steps="flowLineContents.steps"
        :active-step="flowLineContents.activeStep"
      />
    </div>
    <Preview
      class="gift-card__body"
      :card-design-image-url="cardDesignImageUrl"
      :sender-name="senderName"
      :profile-icon-image-url="profileIconImageUrl"
      :profile-link="usableProfileLink"
      :message="message"
      :item-sku-contents="itemSkuContents"
    />
    <div class="gift-card__foot-content">
      <form :action="giftCardContinuePath" method="post">
        <input
          type="hidden"
          name="authenticity_token"
          :value="authenticityToken"
        />
        <input
          type="hidden"
          name="gift_card_session[id]"
          :value="giftCardSessionId"
        />
        <div
          v-if="showUsableProfileLinkSwitch"
          class="gift-card__usable-profile-link-area"
        >
          <div class="gift-card__usable-profile-link__switch-area">
            <span class="gift-card__usable-profile-link__label">
              このギフトカードからあなたのプロフィール画面にアクセスできるようにする
            </span>
            <div class="gift-card__usable-profile-link__switch">
              <ToggleSwitch
                name="gift_card_session[usable_profile_link]"
                :value="usableProfileLink"
                @change-toggle-switch-value="clickUsableProfileLinkSwitch"
              />
            </div>
          </div>
          <a
            class="gift-card__usable-profile-link__link-area"
            @click="showUsableProfileLinkModal"
          >
            <AlertCircleOutline />
            <span class="gift-card__usable-profile-link__link-text">
              設定についての詳細
            </span>
          </a>
          <ProfileLinkModal
            v-if="usableProfileLinkModalDisplayable"
            @clicked-modal-cover="hideUsableProfileLinkModal"
            @clicked-close="hideUsableProfileLinkModal"
          />
        </div>
        <input
          v-else
          type="hidden"
          name="gift_card_session[usable_profile_link]"
          :value="usableProfileLink"
        />
        <div class="gift-card__submit-button-area">
          <button
            v-if="loggedIn"
            class="button btn btn--primary"
            name="submit_type"
            value="continue"
            data-disable-with="決済へ進む"
            @click="clickContinueButton"
          >
            決済へ進む
          </button>
          <button
            v-else
            class="btn btn--primary"
            name="submit_type"
            value="continue"
            data-disable-with="ログイン / 決済へ進む"
            @click="clickLoginAndContinueButton"
          >
            ログイン / 決済へ進む
          </button>
        </div>
      </form>
    </div>
    <div class="gift-card__back-to-edit-area">
      <a id="back-to-edit-gift-card" :href="giftCardEditPath" class="text-link">
        ギフト作成に戻る
      </a>
    </div>
  </div>
</template>
<script>
import PageHeadlineWithFourSteps from '../../molecules/headlines/PageHeadlineWithFourSteps.vue'
import ToggleSwitch from '../../atoms/toggle_switch/ToggleSwitch.vue'
import AlertCircleOutline from '../../atoms/svg_icons/AlertCircleOutline.vue'
import ProfileLinkModal from '../../molecules/modals/profileLink/ProfileLinkModal.vue'
import Preview from '../../molecules/gift_card/Preview.vue'

export default {
  components: {
    PageHeadlineWithFourSteps,
    Preview,
    ToggleSwitch,
    AlertCircleOutline,
    ProfileLinkModal
  },
  props: {
    cardDesignImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    hasProfileIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    profileIconImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    senderName: {
      type: String,
      required: false,
      default: ''
    },
    isMessagePresent: {
      type: Boolean,
      required: false,
      default: false
    },
    message: {
      type: String,
      required: false,
      default: ''
    },
    itemSkuContents: {
      type: Object,
      require: true,
      default: () => {}
    },
    giftCardContinuePath: {
      type: String,
      required: false,
      default: ''
    },
    authenticityToken: {
      type: String,
      required: true
    },
    giftCardSessionId: {
      type: String,
      required: false,
      default: ''
    },
    loggedIn: {
      type: Boolean,
      required: false,
      default: false
    },
    giftCardEditPath: {
      type: String,
      required: false,
      default: ''
    },
    defaultUsableProfileLink: {
      type: Boolean,
      required: false,
      default: false
    },
    showUsableProfileLinkSwitch: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      flowLineContents: {
        steps: ['作成', 'プレビュー', '確認', '完了'],
        activeStep: 2
      },
      usableProfileLink: false,
      usableProfileLinkModalDisplayable: false
    }
  },
  created() {
    this.usableProfileLink = this.defaultUsableProfileLink
  },
  methods: {
    clickContinueButton() {
      dataLayer.push({
        event: 'click_gift_card_preview__draft_order',
        toggle_display: this.showUsableProfileLinkSwitch,
        toggle_onoff: this.usableProfileLink,
        gift_card_session_id: this.giftCardSessionId
      })
    },
    clickLoginAndContinueButton() {
      dataLayer.push({
        event: 'signin_click',
        button: 'preview'
      })
    },
    clickUsableProfileLinkSwitch() {
      this.usableProfileLink = !this.usableProfileLink
    },
    showUsableProfileLinkModal() {
      this.usableProfileLinkModalDisplayable = true
    },
    hideUsableProfileLinkModal() {
      this.usableProfileLinkModalDisplayable = false
    }
  }
}
</script>
<style lang="scss" scoped>
.gift-card-preview-container {
  .gift-card {
    &__head {
      background-color: $white;
      margin-bottom: 16px;
    }

    &__body {
      margin: 0 auto;
      max-width: $small-contents-width;
    }

    &__gray-border {
      height: 16px;
      background-color: $gray-3;
    }

    // gift-card__head 内コンテンツ
    &__flow-line-area {
      padding-bottom: 24px;
      display: flex;
      justify-content: center;
    }

    &__headline {
      margin: 0 auto;
      max-width: $small-contents-width;
      text-align: left;
    }

    // gift-card__buttons 内コンテンツ
    &__submit-button-area {
      padding: 0 16px 16px 16px;
      margin-top: 48px;
    }

    &__back-to-edit-area {
      padding: 16px 0 24px 0;
      display: flex;
      justify-content: center;
    }

    &__foot-content {
      margin: auto;
      box-sizing: border-box;
      max-width: $small-contents-width;
    }

    &__usable-profile-link-area {
      padding: 24px 16px;
      background-color: $white;
      margin-top: 16px;
    }

    &__usable-profile-link {
      &__switch-area {
        display: flex;
        gap: 8px;
        margin-bottom: 8px;
      }

      &__label {
        @extend %body-bold;
        text-align: left;
      }

      &__switch {
        width: 34px;
        height: 20px;
        padding: 10px;
      }

      &__link-area {
        display: flex;
        gap: 4px;
        cursor: pointer;
      }

      &__link-text {
        @extend %hint;
        color: $link;
      }
    }

    @media screen and (max-width: $small-contents-width) {
      &__content,
      &__usable-profile-link-area,
      &__submit-button-area,
      &__headline {
        padding-left: 4.3%;
        padding-right: 4.3%;
      }
    }
  }
}
</style>
